import { Navbar } from '../home/components/Navbar';
import { Footer } from '../home/components/Footer';

export default function ListingSetupPage() {
  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/property-assessment');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#F0F0F0] to-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-xs font-semibold text-[#882629] uppercase tracking-widest mb-6">
            Service Details
          </div>
          <h1 className="font-['Fraunces'] text-5xl lg:text-6xl font-bold text-[#505A5A] mb-8">
            Listing and Setup Support
          </h1>
          <p className="text-xl text-[#505A5A]/80 leading-relaxed max-w-3xl">
            Professional listing creation, pricing guidance, and platform setup—whether you want to self-manage or just need help getting started.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12">
            What's included in your setup
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: 'ri-camera-3-line',
                title: 'Professional photography',
                description: 'High-quality photos that showcase your property\'s best features and attract more bookings. We can arrange a professional photographer or guide you on taking great photos yourself.'
              },
              {
                icon: 'ri-edit-box-line',
                title: 'Compelling listing copy',
                description: 'We write engaging titles and descriptions that highlight your property\'s unique selling points and appeal to your target guests.'
              },
              {
                icon: 'ri-price-tag-3-line',
                title: 'Pricing strategy',
                description: 'We analyze comparable listings in your area and provide a recommended pricing strategy to maximize bookings and revenue.'
              },
              {
                icon: 'ri-settings-3-line',
                title: 'Platform setup',
                description: 'We help you set up your listing on Airbnb, Vrbo, Booking.com, or other platforms, ensuring all settings are optimized for visibility and bookings.'
              },
              {
                icon: 'ri-calendar-line',
                title: 'Calendar and availability',
                description: 'We configure your calendar, availability rules, booking windows, and minimum/maximum stay requirements based on your preferences.'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'House rules and policies',
                description: 'We help you create clear house rules, cancellation policies, and guest requirements to protect your property and set expectations.'
              },
              {
                icon: 'ri-star-line',
                title: 'Amenity optimization',
                description: 'We ensure all your property\'s amenities are properly listed and highlighted to improve search rankings and guest appeal.'
              },
              {
                icon: 'ri-question-answer-line',
                title: 'Self-management guidance',
                description: 'We provide tips, templates, and best practices for managing bookings, communicating with guests, and handling common situations.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#F0F0F0] rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-[#882629] text-4xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                  {item.title}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Professional Setup Matters */}
      <section className="py-20 bg-[#F0F0F0]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12">
            Why professional setup matters
          </h2>
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                First impressions are everything
              </h3>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                Your listing is competing with hundreds of others. Professional photos and compelling copy can increase your booking rate by 40-60% compared to amateur listings.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                Pricing makes or breaks your success
              </h3>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                Price too high and you won\'t get bookings. Price too low and you leave money on the table. Our market analysis ensures you start with competitive, profitable rates.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                Avoid costly mistakes
              </h3>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                Incorrect settings, unclear policies, or missing information can lead to booking issues, guest complaints, and negative reviews. We ensure everything is set up correctly from day one.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                Save time and stress
              </h3>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                Setting up a listing properly takes time and expertise. We handle the technical details so you can focus on preparing your property and welcoming guests.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12">
            Who this service is for
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-user-star-line',
                title: 'First-time hosts',
                description: 'New to short-term rentals and want professional help getting started on the right foot'
              },
              {
                icon: 'ri-hand-heart-line',
                title: 'Self-managers',
                description: 'Want to manage your own property but need expert setup and guidance'
              },
              {
                icon: 'ri-refresh-line',
                title: 'Listing refreshes',
                description: 'Have an existing listing that\'s underperforming and needs optimization'
              },
              {
                icon: 'ri-time-line',
                title: 'Busy owners',
                description: 'Don\'t have time to learn platform intricacies and want it done right'
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: 'Revenue maximizers',
                description: 'Want to ensure your listing is optimized for maximum visibility and bookings'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Risk avoiders',
                description: 'Want to avoid common mistakes that lead to poor reviews or booking issues'
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#F0F0F0] rounded-2xl p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-[#882629] text-4xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-[#505A5A] mb-4">
                  {item.title}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-[#F0F0F0]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12 text-center">
            Flexible pricing options
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-10 shadow-lg">
              <h3 className="text-2xl font-bold text-[#505A5A] mb-4">
                Basic Setup
              </h3>
              <div className="text-4xl font-bold text-[#882629] mb-6">
                $299
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#882629] text-xl mt-1"></i>
                  <span className="text-base text-[#505A5A]/70">Listing copy and description</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#882629] text-xl mt-1"></i>
                  <span className="text-base text-[#505A5A]/70">Pricing strategy and recommendations</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#882629] text-xl mt-1"></i>
                  <span className="text-base text-[#505A5A]/70">Platform setup on one platform</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#882629] text-xl mt-1"></i>
                  <span className="text-base text-[#505A5A]/70">House rules and policies</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#882629] text-xl mt-1"></i>
                  <span className="text-base text-[#505A5A]/70">Self-management guide</span>
                </li>
              </ul>
              <button 
                onClick={scrollToContact}
                className="w-full bg-[#882629] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#6E1F22] transition-all cursor-pointer whitespace-nowrap"
              >
                Get started
              </button>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg border-2 border-[#882629]">
              <div className="text-xs font-semibold text-[#882629] uppercase tracking-widest mb-2">
                Most Popular
              </div>
              <h3 className="text-2xl font-bold text-[#505A5A] mb-4">
                Premium Setup
              </h3>
              <div className="text-4xl font-bold text-[#882629] mb-6">
                $599
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#882629] text-xl mt-1"></i>
                  <span className="text-base text-[#505A5A]/70">Everything in Basic Setup</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#882629] text-xl mt-1"></i>
                  <span className="text-base text-[#505A5A]/70">Professional photography session</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#882629] text-xl mt-1"></i>
                  <span className="text-base text-[#505A5A]/70">Setup on multiple platforms</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#882629] text-xl mt-1"></i>
                  <span className="text-base text-[#505A5A]/70">Advanced pricing optimization</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#882629] text-xl mt-1"></i>
                  <span className="text-base text-[#505A5A]/70">Guest communication templates</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#882629] text-xl mt-1"></i>
                  <span className="text-base text-[#505A5A]/70">30 days of post-launch support</span>
                </li>
              </ul>
              <button 
                onClick={scrollToContact}
                className="w-full bg-[#882629] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#6E1F22] transition-all cursor-pointer whitespace-nowrap"
              >
                Get started
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {[
              {
                question: 'How long does the setup process take?',
                answer: 'Basic setup typically takes 5-7 business days. Premium setup with photography takes 10-14 business days depending on photographer availability.'
              },
              {
                question: 'Can I make changes after the listing is live?',
                answer: 'Absolutely. You have full control over your listing and can make changes anytime. We also provide guidance on ongoing optimization.'
              },
              {
                question: 'Do you offer ongoing management after setup?',
                answer: 'Yes, if you decide you want ongoing management, we offer co-hosting services at 20% commission. The setup fee is credited toward your first month.'
              },
              {
                question: 'What if I already have a listing?',
                answer: 'We can audit and optimize your existing listing. This includes updating photos, rewriting copy, adjusting pricing, and improving settings to boost performance.'
              },
              {
                question: 'Do you help with licensing?',
                answer: 'We provide guidance on licensing requirements, but the application process is your responsibility. If you want full licensing support, consider our compliance review service.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#F0F0F0] rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-[#505A5A] mb-3">
                  {item.question}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#882629]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to launch your listing?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Get professional setup and start earning from your property with confidence.
          </p>
          <button 
            onClick={scrollToContact}
            className="bg-white text-[#882629] px-10 py-4 rounded-full text-base font-medium hover:bg-[#F0F0F0] transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
          >
            Get your listing setup
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
